import type { EngineCall } from './desktop-commands';
import { setupInHand } from './folder-analysis';
import { mirrorReviewPolicy, type MirroredReviewPolicy } from './job-channel';
import { startUnfinishedSetupsOver } from './unfinished-setup';
import { problemWords } from './problem-words';

/** What the adapter reads of the workbench's job definition on save. */
export type WorkbenchJob = {
  id: string;
  name: string;
  reviewPolicy?: MirroredReviewPolicy | null;
};

/** What `POST /v1/jobs` answers: the Paige job, and the workspace job Paige named for it. */
export type CreatedPaigeJob = { id: string; workspaceJobId?: string | null };

/** The two desktop methods this file answers. */
export interface JobCreationCommands {
  listJobs: () => Promise<unknown>;
  /** The job as it now stands: for a job Paige has just made, under the identity Paige gave it. */
  saveJob: (job: WorkbenchJob) => Promise<unknown>;
}

export interface JobCreationOptions {
  /** Where `/engine` answers for the mount: the tenant's channel on the new-job route. */
  engineOrigin: string;
  /** The tenant to act in (the `Paige-Tenant` header). */
  tenant?: string;
  /** The API's root (`/v1`) when a job the workbench creates must become a Paige job too. */
  apiOrigin?: string;
}

/**
 * A job the workbench creates becomes a Paige job first: POST /v1/jobs carries the workbench's
 * definition, the API saves it in the engine under a workspace job id of its own making, and
 * answers with both ids. Which workspace job a request names is the whole tenancy fence on the
 * engine channel (EngineTarget, ADR-0010), so the id the dialog invented is not the job's: the
 * definition handed back carries the server's, and that is the identity the renderer keeps and
 * every later engine call names. Saves of jobs the workspace already knows go straight to the
 * engine, and Paige keeps its own copy of the review policy (mode and confidence bar) for its
 * dashboard and API.
 */
export function jobCreation(
  call: EngineCall,
  { engineOrigin, apiOrigin, tenant }: JobCreationOptions,
  bindChannel: (workspaceJobId: string, paigeJobId: string) => void,
): JobCreationCommands {
  const known = new Set<string>();
  const remember = (jobs: unknown) => {
    if (Array.isArray(jobs)) for (const job of jobs) if (job?.id) known.add(String(job.id));
    return jobs;
  };
  return {
    // A setup from samples that ended without types lists as a job not started (unfinished-setup.ts).
    listJobs: async () => remember(startUnfinishedSetupsOver(await call('list-jobs'), setupInHand)),
    saveJob: async (job: WorkbenchJob) => {
      if (apiOrigin && !known.has(job.id))
        return await create(apiOrigin, tenant, job, known, bindChannel);
      const saved = await call('save-job', { job });
      mirrorReviewPolicy(engineOrigin, tenant, job).catch((reason: unknown) =>
        console.warn('[capturepoint adapter] the review policy was not recorded in Paige', reason),
      );
      return saved;
    },
  };
}

/** The job under the identity Paige gave it, with its channel bound and the route told. */
async function create(
  apiOrigin: string,
  tenant: string | undefined,
  job: WorkbenchJob,
  known: Set<string>,
  bindChannel: (workspaceJobId: string, paigeJobId: string) => void,
): Promise<WorkbenchJob> {
  const created = await createPaigeJob(apiOrigin, tenant, job);
  const saved = { ...job, id: created.workspaceJobId ?? job.id };
  // Both ids are known: the one the dialog invented so a stale caller cannot create a second
  // Paige job with it, and the one the job now has.
  known.add(job.id);
  known.add(saved.id);
  bindChannel(saved.id, created.id);
  window.paigeWorkbench?.created?.(created.id);
  return saved;
}

/** `POST /v1/jobs` with the workbench's definition; the Paige job the API made. */
export async function createPaigeJob(
  apiOrigin: string,
  tenant: string | undefined,
  job: WorkbenchJob,
): Promise<CreatedPaigeJob> {
  const response = await fetch(`${apiOrigin}/jobs`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'Idempotency-Key': job.id,
      ...(tenant ? { 'Paige-Tenant': tenant } : {}),
    },
    body: JSON.stringify({ name: job.name, workspaceJob: job }),
  });
  if (!response.ok) throw new Error(await creationRefusal(response));
  return (await response.json()) as CreatedPaigeJob;
}

/**
 * Paige's reason (the problem's detail) when the job could not be created; a plain line otherwise. A company at its job
 * limit (409 `job_limit_reached`) is also shown Paige's notice with Ademero's address (the bridge's `jobLimit`).
 */
const couldNotCreate =
  'The job could not be created just now. Nothing was lost; try again in a minute.';

export async function creationRefusal(response: Response): Promise<string> {
  try {
    const problem = (await response.json()) as {
      detail?: string;
      title?: string;
      code?: string;
      limit?: number;
    };
    if (problem.code === 'job_limit_reached')
      window.paigeWorkbench?.jobLimit?.(problem.limit ?? 1000);
    return problemWords(response.status, problem, couldNotCreate);
  } catch {
    // Not a problem document: nothing in it was written for a person.
  }
  return couldNotCreate;
}
